import React from "react";
import { Box, Tooltip } from "@mui/material";

/**
 * EllipsizedText
 * - Truncates to a maximum number of lines with CSS ellipsis.
 * - Shows a tooltip ONLY when the content is actually truncated.
 */
const EllipsizedText = ({ text, maxWidth = 300, lines = 2, tooltipPlacement = "top" }) => {
  // Hooks must always run, regardless of props
  const ref = React.useRef(null);
  const [truncated, setTruncated] = React.useState(false);

  const measure = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const isTrunc = el.scrollHeight - el.clientHeight > 1;
    setTruncated(isTrunc);
  }, []);

  React.useEffect(() => {
    measure();
  }, [text, maxWidth, lines, measure]);

  React.useEffect(() => {
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [measure]);

  // After hooks: handle empty content
  if (!text) return "—";

  const content = (
    <Box
      ref={ref}
      sx={{
        maxWidth,
        display: "-webkit-box",
        WebkitLineClamp: lines,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "normal",
      }}
    >
      {text}
    </Box>
  );

  return truncated ? (
    <Tooltip title={text} placement={tooltipPlacement} arrow>
      {content}
    </Tooltip>
  ) : (
    content
  );
};

export default EllipsizedText;
